import React from 'react';
import { Icon } from './Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

/**
 * Step Indicator Component
 *
 * Horizontal step progress indicator with circles and connecting lines.
 * Uses the same proven pattern as CreateProfileWizard.
 */

interface StepIndicatorProps {
  steps: Array<{ id: string; label: string }>;
  currentStepIndex: number;
  completedStepIndexes?: number[];
  onStepClick?: (stepIndex: number) => void;
  className?: string;
}

export const StepIndicator = ({
  steps,
  currentStepIndex,
  completedStepIndexes,
  onStepClick,
  className,
}: StepIndicatorProps) => {
  // Default: all steps before current are completed
  const completed =
    completedStepIndexes ?? Array.from({ length: currentStepIndex }, (_, i) => i);

  const isCompleted = (index: number): boolean => completed.includes(index);
  const isCurrent = (index: number): boolean => index === currentStepIndex;
  const isClickable = (index: number): boolean => isCompleted(index) && !!onStepClick;

  return (
    <div className={cn('flex items-center justify-between gap-1 sm:gap-2 overflow-x-clip overflow-y-visible scrollbar-none', className)}>
      {steps.map((step, index) => {
        const stepCompleted = isCompleted(index);
        const current = isCurrent(index);
        const clickable = isClickable(index);
        // Line before this step is colored if previous step is completed
        const lineCompleted = index <= currentStepIndex;

        return (
          <React.Fragment key={step.id}>
            {/* Connecting line BEFORE step (except first) */}
            {index > 0 && (
              <div
                className={cn(
                  'flex-1 h-px sm:h-0.5 self-start mt-[13px] sm:mt-[15px] min-w-[8px]',
                  lineCompleted ? 'bg-primary-400' : 'bg-gray-200'
                )}
                aria-hidden="true"
              />
            )}

            {/* Step: circle + label stacked */}
            <div className="flex flex-col items-center gap-1 min-w-0 shrink-0">
              {/* Circle */}
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick?.(index)}
                  className={cn(
                    'w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all',
                    'hover:scale-110 cursor-pointer',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                    stepCompleted && 'bg-primary-500 text-primary-contrast',
                    current && 'bg-primary-50 text-primary-600 ring-2 ring-primary-500',
                    !stepCompleted && !current && 'bg-gray-100 text-gray-400'
                  )}
                  aria-label={`Go to step: ${step.label}`}
                  aria-current={current ? 'step' : undefined}
                >
                  {stepCompleted ? (
                    <Icon name={ICONS.CHECK} size="sm" ariaHidden />
                  ) : (
                    index + 1
                  )}
                </button>
              ) : (
                <div
                  className={cn(
                    'w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all',
                    stepCompleted && 'bg-primary-500 text-primary-contrast',
                    current && 'bg-primary-50 text-primary-600 ring-2 ring-primary-500',
                    !stepCompleted && !current && 'bg-gray-100 text-gray-400'
                  )}
                  aria-current={current ? 'step' : undefined}
                >
                  {stepCompleted ? (
                    <Icon name={ICONS.CHECK} size="sm" ariaHidden />
                  ) : (
                    index + 1
                  )}
                </div>
              )}

              {/* Label — hidden on mobile for inactive, always shown for active/completed */}
              <span
                className={cn(
                  'text-[10px] sm:text-xs font-medium text-center max-w-[3.5rem] sm:max-w-[4.5rem] leading-tight',
                  current ? 'text-gray-700' : stepCompleted ? 'hidden sm:block text-gray-500' : 'hidden sm:block text-gray-400'
                )}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
